import AddIcon from '@mui/icons-material/Add';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CloseIcon from '@mui/icons-material/Close';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { FC, useState } from 'react';

import AddReferenceModal from '@features/applicant/components/AddReferenceModal/AddReferenceModal';
import DeleteApplicantReferenceModal from '@features/applicant/components/DeleteApplicantReferenceModal/DeleteApplicantReferenceModal';
import { ApplicantReference } from '@features/applicationQuestions';
import { borderGrey, lightGrey, white } from '@styles/colors';
import { bold, medium } from '@styles/fontWeights';

type ApplicantReferencesListProps = {
  references: ApplicantReference[];
  editMode?: boolean;
};

const ApplicantReferencesList: FC<ApplicantReferencesListProps> = ({
  references,
  editMode = false,
}) => {
  const [referenceToDeleteId, setReferenceToDeleteId] = useState<null | number>(
    null,
  );
  const [isCreateReferenceModalOpen, setIsCreateReferenceModalOpen] =
    useState(false);

  const renderReferencesList = () => {
    if (editMode && !references?.length) {
      return (
        <Box bgcolor={white} p={4} borderRadius={5}>
          <Alert severity="info">
            Adding references will help you stand out amongst other applicants!
          </Alert>
        </Box>
      );
    }

    if (!references?.length) {
      return null;
    }

    return references.map((reference) => (
      <Grid
        container
        key={reference.id}
        sx={{
          bgcolor: white,
          borderWidth: '2px',
          borderColor: borderGrey,
          borderStyle: 'solid',
          borderRadius: 8,
          px: 4,
          py: 3,
          position: 'relative',
        }}
      >
        {editMode && (
          <IconButton
            sx={{ position: 'absolute', right: 15, top: 3 }}
            aria-label="delete"
            onClick={() => setReferenceToDeleteId(reference.id)}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Grid item xs={12} md={editMode ? 12 : 8}>
          <Stack spacing={0.5} alignItems={{ xs: 'center', md: 'start' }}>
            {!reference.dt_referred && (
              <Chip variant="filled" color="error" label="PENDING" />
            )}
            <Typography sx={{ wordBreak: "break-all" }} variant="h3" color="text.primary" fontWeight={bold}>
              {reference.name}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={bold}
            >
              {reference.company} - {reference.position}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={editMode ? 12 : 4}>
          <Stack spacing={0.5} alignItems={{ xs: 'center', md: 'start' }}>
            <Typography
              color="text.secondary"
              display="flex"
              alignItems="center"
              fontWeight={medium}
              sx={{ wordBreak: "break-all" }}
            >
              <AlternateEmailIcon
                color="primary"
                fontSize="inherit"
                sx={{ mr: 1 }}
              />
              {reference.email}
            </Typography>
            <Typography
              color="text.secondary"
              display="flex"
              alignItems="center"
              fontWeight={medium}
              sx={{ wordBreak: "break-all" }}
            >
              <LocalPhoneIcon
                color="primary"
                fontSize="inherit"
                sx={{ mr: 1 }}
              />
              {reference.phone}
            </Typography>
          </Stack>
        </Grid>
        {reference.message && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Box
              bgcolor={lightGrey}
              width="100%"
              height="100%"
              p={4}
              borderRadius={2}
              sx={{ wordBreak: "break-all" }}

            >
              <Typography color="text.secondary" fontWeight={medium}>
                {reference.message}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    ));
  };

  return (
    <Stack spacing={2}>
      <Stack
        spacing={2}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h3" color="text.primary" fontWeight={bold}>
          References
        </Typography>
        {editMode && (
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateReferenceModalOpen(true)}
          >
            Reference
          </Button>
        )}
      </Stack>
      {renderReferencesList()}
      <DeleteApplicantReferenceModal
        referenceId={referenceToDeleteId}
        handleClose={() => setReferenceToDeleteId(null)}
      />
      <AddReferenceModal
        isOpen={isCreateReferenceModalOpen}
        handleClose={() => setIsCreateReferenceModalOpen(false)}
      />
    </Stack>
  );
};

export default ApplicantReferencesList;
